"use client";

import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { LogoTree } from "@/components/brand/LogoTree";
import { navigateWithTransition } from "@/components/shell/TransitionLink";
import { claimSuffix, consoleMessage, consoleTree, easterSwitches, printText, toasts } from "@/content/easter";
import { claimOptions, selectedClaim } from "@/content/home";
import { asuncionHour } from "@/lib/asuncion";
import { fold } from "@/lib/text";
import { worldBus } from "@/lib/world-bus";
import {
  isMidnight,
  isTypingTarget,
  markSecret,
  onToast,
  prefersReducedMotion,
  setMidnight,
  toast,
  type ToastDetail,
} from "./runtime";
import styles from "./easter.module.css";

// Lo único de los easter eggs que vive siempre en la página (02-EASTER-EGGS.md, 1.5):
// escucha teclas, clics en el logo, copias y la inactividad. Cada secreto con interfaz
// (terminal, buscador, tablero, flores, el perro, la tarjeta del dato) se descarga
// recién cuando alguien lo abre.

const Terminal = dynamic(() => import("./terminal/Terminal"), { ssr: false });
const SiteSearch = dynamic(() => import("./search/SiteSearch"), { ssr: false });
const Shortcuts = dynamic(() => import("./Shortcuts"), { ssr: false });
const Chess = dynamic(() => import("./chess/Chess"), { ssr: false });
const Bloom = dynamic(() => import("./Bloom"), { ssr: false });
const Quinto = dynamic(() => import("./Quinto"), { ssr: false });
const RawCard = dynamic(() => import("./RawCard"), { ssr: false });

const KONAMI = ["arrowup", "arrowup", "arrowdown", "arrowdown", "arrowleft", "arrowright", "arrowleft", "arrowright", "b", "a"];
const LOGO_CLICKS = 7;
/** Pausa máxima entre clics para que cuenten como seguidos. */
const LOGO_GAP_MS = 900;
const LONG_PRESS_MS = 2000;
const IDLE_MS = 45000;
const TYPE_GAP_MS = 1500;

// Una sola vez por carga, aunque React monte dos veces en desarrollo.
let consolePrinted = false;

function printConsoleMessage() {
  if (consolePrinted) return;
  consolePrinted = true;
  const mono = "font-family:ui-monospace,Consolas,monospace;";
  console.log(`%c${consoleTree}`, `${mono}color:#7fe1ff;line-height:1.15;text-shadow:0 0 6px rgba(127,225,255,.45)`);
  console.log(
    `%c${consoleMessage.join("\n")}`,
    `${mono}color:#e9eff6;background:#03060c;padding:10px 14px;border-left:2px solid #7fe1ff;line-height:1.6`,
  );
}

type Overlay = "terminal" | "search" | "shortcuts" | "chess";

export function EasterRoot() {
  const router = useRouter();
  const pathname = usePathname();
  const pathRef = useRef(pathname);
  const [terminalMounted, setTerminalMounted] = useState(false);
  const [overlay, setOverlay] = useState<Overlay | null>(null);
  const overlayRef = useRef<Overlay | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const [bloomKey, setBloomKey] = useState(0);
  const [quintoKey, setQuintoKey] = useState(0);
  const [raw, setRaw] = useState<{ el: HTMLElement; spec: string; id: number } | null>(null);
  const [note, setNote] = useState<(ToastDetail & { id: number }) | null>(null);

  useEffect(() => {
    pathRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    overlayRef.current = overlay;
  }, [overlay]);

  const navigate = useCallback((href: string) => navigateWithTransition((h) => router.push(h), href), [router]);

  const open = useCallback((name: Overlay) => {
    const active = document.activeElement;
    // Si ya hay otra capa abierta, el foco vuelve al que abrió la primera.
    if (!overlayRef.current) openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
    if (name === "terminal") {
      setTerminalMounted(true);
      markSecret("terminal");
    }
    if (name === "chess") markSecret("ajedrez");
    overlayRef.current = name;
    setOverlay(name);
  }, []);

  const close = useCallback(() => {
    overlayRef.current = null;
    setOverlay(null);
    const back = openerRef.current;
    openerRef.current = null;
    // El foco vuelve a quien abrió, si sigue en la página y se ve (un botón del menú de
    // celular ya cerrado no sirve).
    window.requestAnimationFrame(() => {
      if (back?.isConnected && back.getClientRects().length) back.focus({ preventScroll: true });
    });
  }, []);

  const bloom = useCallback(() => {
    markSecret("florecer");
    setBloomKey((k) => k + 1);
    toast(toasts.bloom);
  }, []);

  const konami = useCallback(() => {
    if (!easterSwitches.konami) return;
    markSecret("konami");
    // Con movimiento reducido, solo el mensaje (1.3).
    if (!prefersReducedMotion()) setQuintoKey((k) => k + 1);
    toast(toasts.konami);
  }, []);

  // ---- Avisos breves ----
  useEffect(() => {
    let id = 0;
    let timer = 0;
    const off = onToast((d) => {
      window.clearTimeout(timer);
      setNote({ ...d, id: ++id });
      timer = window.setTimeout(() => setNote(null), d.sub ? 5200 : 3600);
    });
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, []);

  // ---- Arranque: consola, Midnight City, hora de Asunción ----
  useEffect(() => {
    printConsoleMessage();
    worldBus.midnight = isMidnight();
    const tick = () => {
      worldBus.hour = asuncionHour(new Date());
    };
    tick();
    const id = window.setInterval(tick, 60000);

    // La consola no avisa cuando se abre. Con las herramientas acopladas, la ventana
    // queda bastante más grande que la página: alcanza para anotar el secreto (solo
    // suma al contador local; un falso positivo no molesta a nadie).
    const checkDevtools = () => {
      if (window.outerWidth - window.innerWidth > 200 || window.outerHeight - window.innerHeight > 260) markSecret("consola");
    };
    checkDevtools();
    window.addEventListener("resize", checkDevtools);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("resize", checkDevtools);
    };
  }, []);

  // ---- Teclado ----
  useEffect(() => {
    let typed = "";
    let typedAt = 0;
    const seq: string[] = [];

    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      // Ctrl/Cmd + K abre el buscador en cualquier parte: no es un secreto, es una función.
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (overlayRef.current !== "search") open("search");
        return;
      }
      if (overlayRef.current || isTypingTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // La tecla debajo de Escape, sea cual sea la distribución: se mira la posición
      // física (code), no el carácter (en español es º, en latinoamericano |).
      if ((e.code === "Backquote" || e.code === "IntlBackslash") && !e.repeat) {
        e.preventDefault();
        open("terminal");
        return;
      }
      if (e.key === "?") {
        e.preventDefault();
        open("shortcuts");
        return;
      }

      const key = e.key.toLowerCase();
      seq.push(key);
      if (seq.length > KONAMI.length) seq.shift();
      if (seq.length === KONAMI.length && seq.every((k, i) => k === KONAMI[i])) {
        seq.length = 0;
        konami();
        return;
      }

      if (e.key.length !== 1) return;
      const now = performance.now();
      if (now - typedAt > TYPE_GAP_MS) typed = "";
      typedAt = now;
      typed = (typed + key).slice(-12);
      if (typed.endsWith("midnight")) {
        typed = "";
        const on = !isMidnight();
        setMidnight(on);
        if (on) toast(toasts.midnightOn, toasts.midnightOnHow);
        else toast(toasts.midnightOff);
      } else if (typed.endsWith("e4") && pathRef.current === "/") {
        typed = "";
        open("chess");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, konami]);

  // ---- Logo: 7 clics florecen; mantenerlo apretado 2 s abre la terminal ----
  useEffect(() => {
    let clicks = 0;
    let lastClick = 0;
    let press: { id: number; x: number; y: number } | null = null;
    let suppressUntil = 0;

    const cancelPress = () => {
      if (press) window.clearTimeout(press.id);
      press = null;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "mouse") return;
      const logo = (e.target as Element | null)?.closest?.("[data-egg-logo]");
      if (!logo) return;
      cancelPress();
      press = {
        x: e.clientX,
        y: e.clientY,
        id: window.setTimeout(() => {
          press = null;
          // El clic que llega al soltar no tiene que navegar.
          suppressUntil = performance.now() + 1500;
          open("terminal");
        }, LONG_PRESS_MS),
      };
    };
    const onPointerMove = (e: PointerEvent) => {
      if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > 12) cancelPress();
    };
    const onContextMenu = (e: MouseEvent) => {
      // Apretar el logo en el celular abre el menú del enlace; mientras dura la presión, no.
      if ((press || performance.now() < suppressUntil) && (e.target as Element | null)?.closest?.("[data-egg-logo]")) e.preventDefault();
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const logo = target?.closest?.("[data-egg-logo]");
      if (logo) {
        if (performance.now() < suppressUntil) {
          suppressUntil = 0;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const now = performance.now();
        clicks = now - lastClick > LOGO_GAP_MS ? 1 : clicks + 1;
        lastClick = now;
        // El séptimo no navega; el octavo y el noveno no hacen nada extra (3.1).
        if (clicks === LOGO_CLICKS) {
          e.preventDefault();
          bloom();
        }
        return;
      }
      const opener = target?.closest?.<HTMLElement>("[data-egg-open]");
      if (opener) {
        const name = opener.dataset.eggOpen as Overlay;
        if (name === "terminal" || name === "search" || name === "shortcuts") {
          e.preventDefault();
          const first = !overlayRef.current;
          open(name);
          // Safari no enfoca los botones al hacer clic: el foco vuelve igual al botón.
          if (first) openerRef.current = opener;
        }
      }
    };

    // En captura y sobre window: corre antes que el enlace de Next, que respeta preventDefault.
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointermove", onPointerMove, true);
    window.addEventListener("pointerup", cancelPress, true);
    window.addEventListener("pointercancel", cancelPress, true);
    window.addEventListener("contextmenu", onContextMenu, true);
    window.addEventListener("click", onClick, true);
    return () => {
      cancelPress();
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", cancelPress, true);
      window.removeEventListener("pointercancel", cancelPress, true);
      window.removeEventListener("contextmenu", onContextMenu, true);
      window.removeEventListener("click", onClick, true);
    };
  }, [open, bloom]);

  // ---- Copiar el claim (3.9) ----
  useEffect(() => {
    if (!easterSwitches.copyClaim) return;
    const claim = fold(claimOptions[selectedClaim].replace(/\.\s*$/, ""));
    const onCopy = (e: ClipboardEvent) => {
      if (isTypingTarget(e.target) || !e.clipboardData) return;
      const text = window.getSelection()?.toString() ?? "";
      const flat = fold(text).replace(/\s+/g, " ");
      if (!flat.includes(claim) || flat.includes(fold(claimSuffix))) return;
      e.clipboardData.setData("text/plain", `${text.trimEnd()} ${claimSuffix}`);
      e.preventDefault();
      toast(toasts.copyClaim);
    };
    document.addEventListener("copy", onCopy);
    return () => document.removeEventListener("copy", onCopy);
  }, []);

  // ---- Doble clic o toque doble en una cifra (3.11) ----
  useEffect(() => {
    let lastTap: { el: Element; t: number; x: number; y: number } | null = null;
    const show = (el: HTMLElement) => {
      const spec = el.dataset.raw;
      if (!spec) return;
      window.getSelection()?.removeAllRanges();
      markSecret("dato");
      setRaw({ el, spec, id: performance.now() });
    };
    const onDbl = (e: MouseEvent) => {
      const el = (e.target as Element | null)?.closest?.<HTMLElement>("[data-raw]");
      if (el) show(el);
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      const el = (e.target as Element | null)?.closest?.<HTMLElement>("[data-raw]");
      if (!el) {
        lastTap = null;
        return;
      }
      const now = performance.now();
      if (lastTap && lastTap.el === el && now - lastTap.t < 350 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 30) {
        lastTap = null;
        show(el);
      } else lastTap = { el, t: now, x: e.clientX, y: e.clientY };
    };
    document.addEventListener("dblclick", onDbl);
    document.addEventListener("pointerup", onUp);
    return () => {
      document.removeEventListener("dblclick", onDbl);
      document.removeEventListener("pointerup", onUp);
    };
  }, []);

  // ---- Salvapantallas (3.6): solo en el inicio y solo si hay escena para mirar ----
  useEffect(() => {
    if (pathname !== "/") return;
    const root = document.documentElement;
    let last = performance.now();
    let idle = false;
    const wake = () => {
      last = performance.now();
      if (!idle) return;
      idle = false;
      delete root.dataset.idle;
      worldBus.idle = false;
    };
    const onVisible = () => {
      if (!document.hidden) wake();
    };
    const id = window.setInterval(() => {
      if (idle || document.hidden || overlayRef.current || worldBus.status !== "ready") return;
      if (performance.now() - last < IDLE_MS) return;
      idle = true;
      root.dataset.idle = "true";
      worldBus.idle = true;
      markSecret("salvapantallas");
    }, 1000);
    const events = ["pointermove", "pointerdown", "keydown", "wheel", "touchstart", "scroll"] as const;
    events.forEach((ev) => window.addEventListener(ev, wake, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      events.forEach((ev) => window.removeEventListener(ev, wake, { capture: true }));
      document.removeEventListener("visibilitychange", onVisible);
      wake();
    };
  }, [pathname]);

  // Estables: los secretos animados cuentan su tiempo desde que se montan.
  const endBloom = useCallback(() => setBloomKey(0), []);
  const endQuinto = useCallback(() => setQuintoKey(0), []);
  const closeRaw = useCallback(() => setRaw(null), []);
  const openSearch = useCallback(() => open("search"), [open]);
  const openTerminal = useCallback(() => open("terminal"), [open]);

  const openChessFromTerminal = useCallback(() => {
    overlayRef.current = "chess";
    markSecret("ajedrez");
    setOverlay("chess");
  }, []);

  return (
    <>
      {terminalMounted && (
        <Terminal open={overlay === "terminal"} onClose={close} navigate={navigate} onChess={openChessFromTerminal} />
      )}
      {overlay === "search" && <SiteSearch onClose={close} navigate={navigate} />}
      {overlay === "shortcuts" && <Shortcuts onClose={close} onSearch={openSearch} onTerminal={openTerminal} />}
      {overlay === "chess" && <Chess onClose={close} />}
      {bloomKey > 0 && <Bloom key={bloomKey} onDone={endBloom} />}
      {quintoKey > 0 && <Quinto key={quintoKey} onDone={endQuinto} />}
      {raw && <RawCard key={raw.id} target={raw.el} spec={raw.spec} onClose={closeRaw} navigate={navigate} />}

      <div className={styles.toast} role="status" aria-live="polite" data-show={note ? "true" : undefined}>
        {note && (
          <p key={note.id} className={styles.toastBody}>
            <span>{note.text}</span>
            {note.sub && <span className={styles.toastSub}>{note.sub}</span>}
          </p>
        )}
      </div>

      {/* Pie de cada hoja impresa (6.5). En pantalla no existe. */}
      <div className="print-foot" aria-hidden="true">
        <div className="print-foot-inner">
          <LogoTree className="print-foot-logo" />
          <span>{printText.foot}</span>
        </div>
      </div>
    </>
  );
}

